import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DownloadIcon from '@mui/icons-material/Download'

import { NavigateTo } from '@contexts/components/NavigateTo'

import chatHook from '@hooks/useChat'
import dialogHook from '@hooks/useDialog'
import errorHandlerHook, { IErrorCallback } from '@hooks/useErrorHandler'
import navigationHook from '@hooks/useNavigation'
import responsiveHook from '@hooks/useResponsive'
import titleHook from '@hooks/useTitle'
import toastHook from '@hooks/useToast'

import PublicWhitePaperService from '@services/public-whitepaper'

import { AuthorAndDateInfo } from '@components/AuthorAndDateInfo'
import { MatchesCountSection } from '@components/MatchesCountSection'
import { SearchBar, SearchBarVariant } from '@components/SearchBar'
import { TLGTypography } from '@components/TLGTypography'

import DownloadsUtils from '@utils/downloads'
import { errorMessages, errorTypes } from '@utils/errors'
import PublicWhitePaperUtils from '@utils/public-whitepapers'
import { LANDING_PAGES } from '@utils/routes'
import { scrollToTop } from '@utils/scroll'
import {
  IPublicWhitePaperWithNeighborSlugs,
  PublicWhitePaperType,
} from '@utils/types'

import {
  breadcrumbTitleSx,
  buttonsWrapperSx,
  containerSx,
  contentGridItemHeaderSx,
  contentGridSx,
  contentSx,
  descriptionWrapperSx,
  imageSx,
  loadingWrapperSx,
  publicWhitePaperAuthorAndDateSx,
  scheduleDemoButtonSx,
  sidebarSx,
  sidebarWrapperSx,
  titleSx,
} from './styles'

import { RecentWhitePapers } from '../RecentWhitePapers'
import { ScheduleADemo } from '../components/ScheduleADemo'
import { LeadMagnetDialog } from './components/LeadMagnetDialog'

type PublicWhitePaperViewType = Pick<
  IPublicWhitePaperWithNeighborSlugs,
  | 'image'
  | 'title'
  | 'description'
  | 'paper'
  | 'paperFileName'
  | 'publishedDate'
  | 'slug'
  | 'neighborPostNavigationSlugs'
  | 'createdByAdmin'
>

export interface IWhitePaperViewProps {
  page: PublicWhitePaperType
}

const WhitePaperView = ({ page }: IWhitePaperViewProps) => {
  const { t } = useTranslation()

  const { handleError } = errorHandlerHook.useErrorHandler()
  const { isMobileOrTablet } = responsiveHook.useResponsive()
  const { setIsShowAssistanceChatBubble } = chatHook.useChat()
  const { navigateTo } = navigationHook.useNavigation()

  const { show: showToast } = toastHook.useToast()

  const { whitePaperSlug } = useParams()

  const downloadWhitePaperDialog = dialogHook.useDialog()

  titleHook.useTitle(
    t(
      `Technology Match - White Papers - ${PublicWhitePaperUtils.getPublicWhitePaperTypeDisplayString(
        page
      )} - ${whitePaperSlug}`
    )
  )
  const rootUrl = `/whitepapers/${page}`

  useEffect(() => {
    setIsShowAssistanceChatBubble(true)
  }, [setIsShowAssistanceChatBubble])

  const [isLoading, setIsLoading] = useState(true)

  const [publicWhitePaper, setPublicWhitePaper] =
    useState<PublicWhitePaperViewType>()

  const getPrevPostSlug = () =>
    publicWhitePaper?.neighborPostNavigationSlugs.prevPostSlug
  const getNextPostSlug = () =>
    publicWhitePaper?.neighborPostNavigationSlugs.nextPostSlug

  const showPublicWhitePaperNotFoundToast = useCallback(() => {
    showToast({
      type: 'error',
      message: errorMessages[errorTypes.WHITEPAPER_NOT_FOUND],
    })
  }, [showToast])

  useLayoutEffect(() => {
    if (publicWhitePaper) {
      setTimeout(() => {
        scrollToTop()
      }, 100)
    }
  }, [publicWhitePaper])

  const fetchPublicWhitePaper = useCallback(async () => {
    if (!whitePaperSlug) {
      showPublicWhitePaperNotFoundToast()
      return
    }

    setIsLoading(true)
    try {
      const response = await PublicWhitePaperService.findBySlugAsVisitor(
        whitePaperSlug
      )

      setPublicWhitePaper(response)
    } catch (err) {
      const publicWhitePaperNotFoundErrorCallback: IErrorCallback = {
        type: errorTypes.WHITEPAPER_NOT_FOUND,
        callback: () => {
          showPublicWhitePaperNotFoundToast()
          navigateTo({ path: LANDING_PAGES.whitepapers })
        },
      }

      handleError({
        exception: err,
        errorCallbacks: [publicWhitePaperNotFoundErrorCallback],
      })
    }

    setIsLoading(false)
  }, [
    whitePaperSlug,
    handleError,
    navigateTo,
    showPublicWhitePaperNotFoundToast,
  ])

  useEffect(() => {
    fetchPublicWhitePaper()
  }, [fetchPublicWhitePaper])

  const handleOnSearch = (searchText: string) => {
    navigateTo({ path: `${rootUrl}/?search=${searchText}` })
  }

  const navigateToRootUrl = () => {
    navigateTo({ path: rootUrl })
  }

  const handleOnOlderButtonClick = () => {
    const slug = getPrevPostSlug()

    if (slug) {
      navigateTo({ path: `${rootUrl}/${slug}` })
    }
  }

  const handleOnNewerButtonClick = () => {
    const slug = getNextPostSlug()

    if (slug) {
      navigateTo({ path: `${rootUrl}/${slug}` })
    }
  }

  if (!whitePaperSlug) {
    return <NavigateTo path={rootUrl} />
  }

  const handleOnSubmit = () => {
    DownloadsUtils.downloadFromUrl({
      url: publicWhitePaper!.paper as string,
      fileName: publicWhitePaper!.paperFileName,
    })
  }

  return (
    <>
      <LeadMagnetDialog
        isOpen={downloadWhitePaperDialog.isOpen}
        onClose={downloadWhitePaperDialog.close}
        onSubmit={handleOnSubmit}
        title={t(
          'Unlocking the Full Whitepaper: A Guide to Downloading In-Depth Insights'
        )}
      />

      <Box sx={containerSx}>
        <Stack sx={contentSx}>
          <Box
            display="grid"
            gridTemplateColumns="repeat(26, 1fr)"
            sx={contentGridSx}
          >
            <Box gridColumn={{ xs: 'span 26', lg: 'span 15', xl: 'span 18' }}>
              {isLoading ? (
                <Stack sx={loadingWrapperSx}>
                  <CircularProgress />
                </Stack>
              ) : (
                <>
                  <Grid
                    container
                    rowSpacing={{ xs: '2.4rem', md: '5.2rem' }}
                    columnSpacing={{ xs: '1.6rem', lg: '2.4rem' }}
                  >
                    <Grid item xs={12}>
                      <Stack sx={contentGridItemHeaderSx}>
                        <TLGTypography
                          data-testid="breadcrumb-title-link"
                          variant="h6"
                          component="h1"
                          sx={breadcrumbTitleSx}
                          onClick={navigateToRootUrl}
                        >
                          {t(
                            `White Papers / ${PublicWhitePaperUtils.getPublicWhitePaperTypeDisplayString(
                              page
                            )}`
                          )}
                        </TLGTypography>
                        <Typography
                          data-testid="white-paper-title"
                          component="h1"
                          variant="h3"
                          sx={titleSx}
                        >
                          {publicWhitePaper?.title}
                        </Typography>
                        <Stack>
                          <Divider />

                          <TLGTypography
                            data-testid="white-paper-author-and-date"
                            variant="subtitle1"
                            title={t('Publication Info')}
                            sx={publicWhitePaperAuthorAndDateSx}
                          >
                            <AuthorAndDateInfo
                              author={publicWhitePaper?.createdByAdmin}
                              publishedDate={publicWhitePaper?.publishedDate}
                            />
                          </TLGTypography>
                        </Stack>
                        {isMobileOrTablet && (
                          <SearchBar
                            variant={SearchBarVariant.LANDING_PAGE}
                            placeholder="Search White Paper"
                            onSearch={handleOnSearch}
                            maxLength={128}
                          />
                        )}
                      </Stack>
                      <Box
                        data-testid="white-paper-image"
                        loading="lazy"
                        component="img"
                        src={publicWhitePaper?.image as string}
                        alt={t('Launchpad Logo')}
                        sx={imageSx}
                      />
                      {publicWhitePaper?.description && (
                        <Box
                          data-testid="white-paper-description"
                          sx={descriptionWrapperSx}
                        >
                          <TLGTypography variant="body1">
                            {publicWhitePaper?.description}
                          </TLGTypography>
                        </Box>
                      )}
                      <Button
                        data-testid="download-white-paper-button"
                        variant="contained"
                        color="secondary"
                        size="large"
                        endIcon={<DownloadIcon />}
                        fullWidth
                        onClick={downloadWhitePaperDialog.open}
                        sx={scheduleDemoButtonSx}
                      >
                        {t('Download White Paper')}
                      </Button>
                      <Stack sx={buttonsWrapperSx}>
                        <Button
                          data-testid="older-button"
                          variant="outlined"
                          size="medium"
                          startIcon={<ChevronLeftIcon />}
                          onClick={handleOnOlderButtonClick}
                          disabled={
                            !publicWhitePaper?.neighborPostNavigationSlugs
                              ?.prevPostSlug
                          }
                        >
                          {t('Older')}
                        </Button>
                        <Button
                          data-testid="newer-button"
                          variant="outlined"
                          size="medium"
                          endIcon={<ChevronRightIcon />}
                          onClick={handleOnNewerButtonClick}
                          disabled={
                            !publicWhitePaper?.neighborPostNavigationSlugs
                              ?.nextPostSlug
                          }
                        >
                          {t('Newer')}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
            <Box
              gridColumn={{
                xs: 'span 26',
                lg: 'span 9 / 27',
                xl: 'span 6 / 27',
              }}
              sx={sidebarSx}
            >
              <Stack data-testid="white-papers-sidebar" sx={sidebarWrapperSx}>
                {!isMobileOrTablet && (
                  <SearchBar
                    variant={SearchBarVariant.LANDING_PAGE}
                    placeholder="Search White Paper"
                    onSearch={handleOnSearch}
                    maxLength={128}
                  />
                )}
                <RecentWhitePapers whitePaperType={page} />
                <ScheduleADemo />
                <MatchesCountSection variant="sidebar" />
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
    </>
  )
}

export { WhitePaperView }
